/**
 * The New chat row, and the Recents filter that grows out of its magnifier.
 * One 32px row in both states, so opening the filter moves nothing: the field
 * widens from the magnifier across the row and shrinks back into it (200ms,
 * ease-in-out), and New chat fades under it. Escape, or leaving it empty,
 * closes it. The chat.search shortcut opens the filter wherever this row is
 * mounted, reopening a collapsed desktop sidebar. Below md the sheet unmounts
 * this row while it is closed, so there the shortcut works only with it open.
 *
 * @module components/home/HomeSidebarNewChatRow
 */
import { useAtomValue } from "@effect/atom-react";
import { SearchIcon, SquarePenIcon } from "lucide-react";
import { useEffect, useRef } from "react";

import { cn } from "~/lib/utils";
import { resolveShortcutCommand, shortcutLabelForCommand } from "../../keybindings";
import { isTerminalFocused } from "../../lib/terminalFocus";
import { primaryServerKeybindingsAtom } from "../../state/server";
import { SidebarMenu, SidebarMenuItem, useSidebar } from "../ui/sidebar";
import { SidebarRow } from "../ui/sidebar-row";
import { Tooltip, TooltipPopup, TooltipTrigger } from "../ui/tooltip";

export function HomeSidebarNewChatRow({
  onNewChat,
  newChatDisabled,
  newChatActive,
  filter,
  onFilterChange,
}: {
  readonly onNewChat: () => void;
  readonly newChatDisabled: boolean;
  readonly newChatActive: boolean;
  /** The Recents filter text, or null while the row is New chat. */
  readonly filter: string | null;
  readonly onFilterChange: (filter: string | null) => void;
}) {
  const open = filter !== null;
  const inputRef = useRef<HTMLInputElement | null>(null);
  const keybindings = useAtomValue(primaryServerKeybindingsAtom);
  const { isMobile, open: sidebarOpen, setOpen: setSidebarOpen } = useSidebar();
  const shortcut = isMobile ? null : shortcutLabelForCommand(keybindings, "chat.search");

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.defaultPrevented) return;
      const command = resolveShortcutCommand(event, keybindings, {
        context: { terminalFocus: isTerminalFocused() },
      });
      if (command !== "chat.search") return;
      event.preventDefault();
      event.stopPropagation();
      if (!isMobile && !sidebarOpen) setSidebarOpen(true);
      if (!open) onFilterChange("");
      requestAnimationFrame(() => {
        inputRef.current?.focus({ preventScroll: true });
        inputRef.current?.select();
      });
    };
    // Capture, so the composer cannot take the key first.
    window.addEventListener("keydown", onKeyDown, true);
    return () => window.removeEventListener("keydown", onKeyDown, true);
  }, [isMobile, keybindings, onFilterChange, open, setSidebarOpen, sidebarOpen]);

  // Focus the filter as it opens, not on every render while it is open: the
  // row re-renders on navigation, and focus must stay where the person put it.
  useEffect(() => {
    if (open && inputRef.current && document.activeElement !== inputRef.current) {
      inputRef.current.focus({ preventScroll: true });
    }
  }, [open]);

  return (
    <div className="relative flex h-8 items-center gap-1 ps-px" data-home-new-chat-row="" data-filter-open={open || undefined}>
      <SidebarMenu
        className={cn(
          "min-w-0 flex-1 gap-0.5 transition-opacity duration-200 ease-in-out motion-reduce:transition-none",
          open && "pointer-events-none opacity-0",
        )}
        inert={open}
      >
        <SidebarMenuItem>
          <SidebarRow data-home-new-chat="" data-tour="new-chat" isActive={newChatActive} disabled={newChatDisabled} onClick={onNewChat}>
            <SquarePenIcon />
            <span className="truncate">New chat</span>
          </SidebarRow>
        </SidebarMenuItem>
      </SidebarMenu>
      <span aria-hidden className="size-8 shrink-0" />
      <div
        data-recents-filter=""
        className={cn(
          // Closed, the box sits 2px in so its magnifier ends on the times' right edge; open, it spans the row.
          "absolute inset-y-0 flex items-center overflow-hidden rounded-md transition-[width,right,background-color] duration-200 ease-in-out motion-reduce:transition-none",
          open ? "end-0 w-[calc(100%-1px)] bg-sidebar-row-hover" : "end-0.5 w-8 hover:bg-sidebar-row-hover",
        )}
      >
        <SearchIcon
          aria-hidden
          strokeWidth={1.75}
          className={cn(
            "pointer-events-none absolute top-1/2 size-4 -translate-y-1/2 transition-[left,color] duration-200 ease-in-out motion-reduce:transition-none",
            open ? "left-2.5 text-sidebar-foreground" : "left-2 text-[var(--sidebar-icon-color)]",
          )}
        />
        <input
          ref={inputRef}
          type="text"
          value={filter ?? ""}
          tabIndex={open ? 0 : -1}
          aria-hidden={!open || undefined}
          aria-label="Filter chats"
          placeholder="Filter chats…"
          spellCheck={false}
          autoComplete="off"
          onChange={(event) => onFilterChange(event.target.value)}
          onKeyDown={(event) => {
            if (event.key !== "Escape") return;
            event.preventDefault();
            event.stopPropagation();
            onFilterChange(null);
          }}
          onBlur={(event) => {
            if (event.target.value.trim() === "") onFilterChange(null);
          }}
          className={cn(
            "h-full min-w-0 flex-1 bg-transparent ps-[34px] pe-2.5 text-[13px] text-sidebar-foreground outline-none transition-opacity duration-200 ease-in-out placeholder:text-sidebar-muted-foreground/60 motion-reduce:transition-none",
            open ? "opacity-100 delay-75" : "pointer-events-none opacity-0",
          )}
        />
        {open ? null : (
          <Tooltip>
            <TooltipTrigger
              render={
                <button
                  type="button"
                  aria-label="Filter chats"
                  data-recents-filter-toggle=""
                  onClick={() => onFilterChange("")}
                  className="absolute inset-0 cursor-pointer rounded-md outline-hidden focus-visible:ring-2 focus-visible:ring-ring"
                />
              }
            />
            <TooltipPopup side="bottom">Filter chats{shortcut ? ` (${shortcut})` : ""}</TooltipPopup>
          </Tooltip>
        )}
      </div>
    </div>
  );
}
